import { useStore } from '@nanostores/react';
import { memo, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PromptEditor } from '~/components/dialogs/settings/PromptEditor';
import { SectionContainer } from '~/components/dialogs/settings/SectionContainer';
import { LoadingDots } from '~/components/ui/LoadingDots';
import { BOLT_PROMPT_PATH } from '~/lib/constants';
import { useStateTracked } from '~/lib/hooks/useStateTracked';
import { projectStore } from '~/lib/stores/project';
import { templateStore } from '~/lib/stores/template';
import { workbenchStore } from '~/lib/stores/workbench';
import { logger } from '~/utils/logger';
import { withSpinner } from '~/utils/withSpinner';

function readPromptFile() {
  try {
    const { content } = workbenchStore.readFile(BOLT_PROMPT_PATH);

    return content.trim();
  } catch {
    return '';
  }
}

interface KnowledgeSettingsProps {
  projectId?: string;
}

export const KnowledgeProject = memo<KnowledgeSettingsProps>(() => {
  const projectId = useStore(projectStore.projectId);

  const [loadingProjectPrompt, setLoadingProjectPrompt] = useState(
    () => !workbenchStore.fileImportComplete || !templateStore.template,
  );

  const [localProjectPrompt, setLocalProjectPrompt, localProjectPromptChanged] = useStateTracked(() => {
    return workbenchStore.fileImportComplete ? readPromptFile() : '';
  });

  useEffect(() => {
    if (!workbenchStore.fileImportComplete || !templateStore.template) {
      withSpinner({
        action: async () => {
          await workbenchStore.waitForImport();
          await templateStore.ready;

          setLocalProjectPrompt(readPromptFile(), true);
        },
        onStateUpdate: setLoadingProjectPrompt,
      });
    }
  }, []);

  const handleProjectPromptChange = useCallback(async (value: string) => {
    try {
      const trimmedValue = value.trim();

      await workbenchStore.writeFile(BOLT_PROMPT_PATH, { type: 'file', content: trimmedValue });

      setLocalProjectPrompt(trimmedValue, true);

      toast.success('Project prompt updated');
    } catch (error) {
      logger.error(error);
      toast.error('Failed to update prompt');
    }
  }, []);

  return (
    <SectionContainer title="Project Knowledge" className="flex flex-col h-full overflow-hidden pb-6">
      <div className="flex-1">
        {loadingProjectPrompt ? (
          <LoadingDots text="Loading project prompt" />
        ) : (
          <PromptEditor
            value={localProjectPrompt}
            disabled={!projectId}
            saveDisabled={!localProjectPromptChanged}
            placeholder="Define project-specific instructions that Bolt will follow when working on this project. This helps tailor Bolt's responses to your project's unique context and requirements."
            description={
              !projectId ? 'Start chatting with Bolt and your project prompt will show up here' : 'Project instructions'
            }
            onSave={handleProjectPromptChange}
            setValue={setLocalProjectPrompt}
            innerClassName="h-full"
          />
        )}
      </div>
    </SectionContainer>
  );
});
